import MicRecorder from "mic-recorder-to-mp3"
import { useEffect, useState, useRef, Fragment, useCallback, useMemo, useReducer } from "react"
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import BackdropProgress from '../components/BackdropProgress'
import IconButton from '@mui/material/IconButton'
import Snackbar from '@mui/material/Snackbar'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'

import { Mic, Undo2, FileText, ChevronDown, X, ArrowLeft, ArrowRight } from 'lucide-react'

import { encodeMP3, applyTemplate, conciseReport, feedbackReport, generateReport } from "../actions/encodeActions"
import { ADD_SAMPLE_PCR, ENCODE_RESET, RESET_GENERATED_PCR, RESET_APPLY_TEMPLATE, RESET_CONCISE_REPORT, RESET_REPORT_FEEDBACK } from "../constants/encodeConstants"
import { REFRESH_CREATE_SCREEN_RESET } from "../constants/createConstants"
import { OPEN_CREATE_PCR_DIALOG } from '../constants/userConstants'
import SEO from '../components/SEO'
import './CreateScreen.css'

const reportType = ['', 'SOAP', 'CHART', 'DCHART']

// The narrative bounds enforced in nextStep — named so the counter can't drift.
const MIN_CHARS = 10
const MAX_CHARS = 10000

// Sample narratives for different scenarios
const sampleNarratives = [
    {
        title: "Chest Pain - Cardiac",
        content: `Dispatched to private residence for 65 y/o male c/o chest pain and dizziness.
Upon arrival, patient found sitting upright on couch, alert and oriented x3.
Patient reports onset of substernal chest pressure approximately 45 minutes prior, radiating to left arm.
Denies shortness of breath, nausea, or recent trauma.
Vitals: BP 148/92, HR 88, RR 18, SpO2 97% on room air.
Lung sounds clear bilaterally, no JVD, no pedal edema noted.
12-lead EKG obtained, showing ST depression in lateral leads.
Aspirin 324 mg administered PO per protocol, IV established in left AC with NS TKO.
Patient transported emergent to local cardiac center with continuous monitoring en route.
Condition remained stable during transport; report given to ED staff without incident.`
    },
    {
        title: "Motor Vehicle Accident",
        content: `Dispatched to MVA with injuries at intersection of Main St and Oak Ave.
Upon arrival, found single vehicle vs tree, moderate front-end damage.
Driver, 34 y/o female, found walking around vehicle, appears anxious but cooperative.
Reports sudden onset of dizziness prior to impact, denies loss of consciousness.
Patient c/o neck pain and mild headache, denies chest pain or shortness of breath.
Vitals: BP 136/84, HR 102, RR 20, SpO2 98% on room air, Temp 98.6°F.
Applied cervical collar per protocol, immobilized on backboard.
Established IV access, administered NS at KVO rate.
Patient transported to trauma center for further evaluation.
Remained stable throughout transport with continuous monitoring.`
    },
    {
        title: "Respiratory Distress",
        content: `Responded to elderly care facility for 78 y/o female with acute respiratory distress.
Patient found in bed, sitting upright, using accessory muscles, audible wheeze.
History of COPD and CHF, reports worsening shortness of breath over past 2 hours.
Patient alert but anxious, speaking in 2-3 word sentences.
Vitals: BP 162/98, HR 118, RR 28, SpO2 88% on room air.
Administered high-flow O2 via NRB mask, SpO2 improved to 94%.
Lung sounds reveal bilateral wheezes and crackles in lower lobes.
Albuterol nebulizer treatment given per protocol with good response.
Established IV access, transported to hospital with continuous monitoring.`
    },
    {
        title: "Diabetic Emergency",
        content: `Dispatched to private residence for unresponsive person, possible diabetic emergency.
Upon arrival, found 42 y/o male lying on kitchen floor, wife states patient collapsed.
Patient responsive to verbal stimuli, confused and diaphoretic.
Wife reports patient is diabetic, missed lunch, took insulin as usual this morning.
Vitals: BP 118/76, HR 96, RR 16, SpO2 99% on room air.
Blood glucose reading: 38 mg/dL via glucometer.
Administered D50W 25g IV push per protocol with immediate improvement.
Patient became alert and oriented within 5 minutes of treatment.
Transported to hospital for further evaluation, remained stable en route.`
    },
    {
        title: "Syncope/Fall",
        content: `Responded to grocery store for elderly fall, possible syncope.
Found 82 y/o female sitting on floor near checkout, assisted by store staff.
Patient reports sudden dizziness and weakness, then "next thing I knew I was down."
Denies chest pain, shortness of breath, or palpitations prior to fall.
Patient alert and oriented x3, minor abrasion to right knee noted.
Vitals: BP 94/58, HR 58, RR 18, SpO2 96% on room air.
Applied bandage to knee abrasion, assisted patient to stretcher.
Established IV access with NS at KVO rate for hypotension.
Transported to hospital for evaluation, vital signs remained stable.`
    }
];

// Initial state for useReducer
const initialState = {
    stepValue: 0,
    maxStepValue: 0,
    allTranscribes: '',
    prevAllTranscribes: [],
    originalChartText: '',
    editableChartText: '',
    editTemplateInput: '',
    editConciseInput: '',
    editFeedbackInput: '',
    lastTemplateInput: '',
    lastConciseInput: '',
    openSnackbar: false,
    snackbarMessage: '',
    reportConfig: [],
    // Add menu state
    anchorEl: null,
    menuOpen: false
}

// Action types
const ACTION_TYPES = {
    SET_STEP: 'SET_STEP',
    SET_MAX_STEP: 'SET_MAX_STEP',
    UPDATE_TRANSCRIBES: 'UPDATE_TRANSCRIBES',
    ADD_TRANSCRIBE: 'ADD_TRANSCRIBE',
    UNDO_TRANSCRIBE: 'UNDO_TRANSCRIBE',
    SET_CHART_TEXT: 'SET_CHART_TEXT',
    SET_EDITABLE_CHART_TEXT: 'SET_EDITABLE_CHART_TEXT',
    SET_TEMPLATE_INPUT: 'SET_TEMPLATE_INPUT',
    SET_CONCISE_INPUT: 'SET_CONCISE_INPUT',
    SET_FEEDBACK_INPUT: 'SET_FEEDBACK_INPUT',
    SET_LAST_TEMPLATE_INPUT: 'SET_LAST_TEMPLATE_INPUT',
    SET_LAST_CONCISE_INPUT: 'SET_LAST_CONCISE_INPUT',
    SHOW_SNACKBAR: 'SHOW_SNACKBAR',
    HIDE_SNACKBAR: 'HIDE_SNACKBAR',
    SET_REPORT_CONFIG: 'SET_REPORT_CONFIG',
    RESET_ALL_TEXTS: 'RESET_ALL_TEXTS',
    RESET_ALL_STATE: 'RESET_ALL_STATE',
    // Add menu actions
    OPEN_MENU: 'OPEN_MENU',
    CLOSE_MENU: 'CLOSE_MENU'
}

// Reducer function
function createScreenReducer(state, action) {
    switch (action.type) {
        case ACTION_TYPES.SET_STEP:
            return { ...state, stepValue: action.payload }
        case ACTION_TYPES.SET_MAX_STEP:
            return { ...state, maxStepValue: action.payload }
        case ACTION_TYPES.UPDATE_TRANSCRIBES:
            return { ...state, allTranscribes: action.payload }
        case ACTION_TYPES.ADD_TRANSCRIBE:
            return {
                ...state,
                prevAllTranscribes: [...state.prevAllTranscribes, state.allTranscribes],
                allTranscribes: state.allTranscribes + action.payload + '\n'
            }
        case ACTION_TYPES.UNDO_TRANSCRIBE:
            if (state.prevAllTranscribes.length > 0) {
                const newPrev = [...state.prevAllTranscribes]
                const lastText = newPrev.pop()
                return {
                    ...state,
                    prevAllTranscribes: newPrev,
                    allTranscribes: lastText
                }
            }
            return state
        case ACTION_TYPES.SET_CHART_TEXT:
            return {
                ...state,
                originalChartText: action.payload,
                editableChartText: action.payload
            }
        case ACTION_TYPES.SET_EDITABLE_CHART_TEXT:
            return { ...state, editableChartText: action.payload }
        case ACTION_TYPES.SET_TEMPLATE_INPUT:
            return { ...state, editTemplateInput: action.payload }
        case ACTION_TYPES.SET_CONCISE_INPUT:
            return { ...state, editConciseInput: action.payload }
        case ACTION_TYPES.SET_FEEDBACK_INPUT:
            return { ...state, editFeedbackInput: action.payload }
        case ACTION_TYPES.SET_LAST_TEMPLATE_INPUT:
            return { ...state, lastTemplateInput: action.payload }
        case ACTION_TYPES.SET_LAST_CONCISE_INPUT:
            return { ...state, lastConciseInput: action.payload }
        case ACTION_TYPES.SHOW_SNACKBAR:
            return { ...state, openSnackbar: true, snackbarMessage: action.payload }
        case ACTION_TYPES.HIDE_SNACKBAR:
            return { ...state, openSnackbar: false }
        case ACTION_TYPES.SET_REPORT_CONFIG:
            return { ...state, reportConfig: action.payload }
        case ACTION_TYPES.RESET_ALL_TEXTS:
            return {
                ...state,
                originalChartText: '',
                editableChartText: '',
                allTranscribes: ''
            }
        case ACTION_TYPES.RESET_ALL_STATE:
            return {
                ...initialState
            }
        // Add menu cases
        case ACTION_TYPES.OPEN_MENU:
            return { ...state, anchorEl: action.payload, menuOpen: true }
        case ACTION_TYPES.CLOSE_MENU:
            return { ...state, anchorEl: null, menuOpen: false }
        default:
            return state
    }
}

// Custom hook for recording logic
const useRecording = () => {
    const recorder = useRef(null)
    const [isRecording, setIsRecording] = useState(false)

    useEffect(() => {
        if (!recorder.current) {
            recorder.current = new MicRecorder({ bitRate: 128 })
        }
    }, [])

    const startRecording = useCallback(() => {
        recorder.current.start().then(() => {
            setIsRecording(true)
        })
    }, [])

    const stopRecording = useCallback(() => {
        return recorder.current.stop().getMp3().then(([buffer, blob]) => {
            const file = new File(buffer, "audio.mp3", {
                type: blob.type,
                lastModified: Date.now(),
            })
            setIsRecording(false)
            return file
        })
    }, [])

    return { isRecording, startRecording, stopRecording }
}

function CreateScreen() {
    const location = useLocation();
    const params = location.state || {};

    const storedReportTypeId = Number(localStorage.getItem('lastReportTypeId') || 0)
    const reportTypeId = Number(params.report || storedReportTypeId)
    const template = Number(params.template || -1)

    const [state, localDispatch] = useReducer(createScreenReducer, initialState)
    const { isRecording, startRecording, stopRecording } = useRecording()

    const dispatch = useDispatch()

    // Redux selectors - using more granular selection
    const userInfo = useSelector(state => state.userLogin.userInfo)
    const { loading: encodeLoading, text: encodeText, encodeError } = useSelector(state => state.encodeReducer)
    const { chartError, chartLoading, chartText } = useSelector(state => state.reportReducer)
    const { templateError, applyingTemplate, templateText } = useSelector(state => state.templateReducer)
    const { conciseError, concisingText, conciseText } = useSelector(state => state.conciseTextReducer)
    const { feedbackError, feedbackInprogress, feedbackText } = useSelector(state => state.feedbackTextReducer)
    const refreshing = useSelector(state => state.createReducer.refreshing)

    // Combined loading state
    const isLoading = useMemo(() =>
        encodeLoading || chartLoading || applyingTemplate || concisingText || feedbackInprogress,
        [encodeLoading, chartLoading, applyingTemplate, concisingText, feedbackInprogress]
    )

    // Combined error state
    const errors = useMemo(() =>
        [encodeError, chartError, templateError, conciseError, feedbackError].filter(Boolean),
        [encodeError, chartError, templateError, conciseError, feedbackError]
    )

    // Handle refresh
    useEffect(() => {
        if (reportTypeId !== 0) {
            localStorage.setItem('lastReportTypeId', reportTypeId)
        } else {
            dispatch({ type: OPEN_CREATE_PCR_DIALOG })
        }
    }, [reportTypeId])

    useEffect(() => {
        if (refreshing) {
            dispatch({ type: REFRESH_CREATE_SCREEN_RESET })
            // Reset all Redux states
            dispatch({ type: RESET_GENERATED_PCR })
            dispatch({ type: RESET_APPLY_TEMPLATE })
            dispatch({ type: RESET_CONCISE_REPORT })
            dispatch({ type: RESET_REPORT_FEEDBACK })
            dispatch({ type: ENCODE_RESET })
            // Reset local component state
            localDispatch({ type: ACTION_TYPES.RESET_ALL_STATE })
        }
    }, [refreshing, dispatch])

    // Handle chart text updates
    useEffect(() => {
        if (chartText?.Original?.Combined) {
            localDispatch({ type: ACTION_TYPES.SET_CHART_TEXT, payload: chartText.Original.Combined })
            localDispatch({ type: ACTION_TYPES.SET_REPORT_CONFIG, payload: chartText.ReportConfig || [] })
            dispatch({ type: RESET_GENERATED_PCR })
        }
    }, [chartText, dispatch])

    // Handle encoded text
    useEffect(() => {
        if (encodeText) {
            localDispatch({ type: ACTION_TYPES.ADD_TRANSCRIBE, payload: encodeText })
            dispatch({ type: ENCODE_RESET })
        }
    }, [encodeText, dispatch])

    // Handle template text
    useEffect(() => {
        if (templateText?.Template?.Combined && !state.editTemplateInput) {
            localDispatch({ type: ACTION_TYPES.SET_TEMPLATE_INPUT, payload: templateText.Template.Combined })
        }
    }, [templateText, state.editTemplateInput])

    // Handle concise text
    useEffect(() => {
        if (conciseText?.Concise?.Combined && !state.editConciseInput) {
            localDispatch({ type: ACTION_TYPES.SET_CONCISE_INPUT, payload: conciseText.Concise.Combined })
        }
    }, [conciseText, state.editConciseInput])

    // Handle feedback text
    useEffect(() => {
        if (feedbackText?.Feedback?.Combined && !state.editFeedbackInput) {
            localDispatch({ type: ACTION_TYPES.SET_FEEDBACK_INPUT, payload: feedbackText.Feedback.Combined })
        }
    }, [feedbackText, state.editFeedbackInput])

    // Event handlers using useCallback
    const handleStartStop = useCallback(async () => {
        if (!isRecording) {
            startRecording()
        } else {
            const file = await stopRecording()
            dispatch(encodeMP3(file))
        }
    }, [isRecording, startRecording, stopRecording, dispatch])

    const handleGenerate = useCallback(() => {
        const userId = userInfo?.id || -1
        dispatch(generateReport(state.allTranscribes, userId, reportTypeId))
    }, [state.allTranscribes, userInfo, reportTypeId, dispatch])

    const handleApplyTemplate = useCallback((userId) => {
        if (state.lastTemplateInput !== state.editableChartText || state.editableChartText === '') {
            localDispatch({ type: ACTION_TYPES.SET_TEMPLATE_INPUT, payload: '' })
            dispatch(applyTemplate(state.editableChartText, userId, reportTypeId, template, state.reportConfig))
            localDispatch({ type: ACTION_TYPES.SET_LAST_TEMPLATE_INPUT, payload: state.editableChartText })
        }
    }, [state.lastTemplateInput, state.editableChartText, state.reportConfig, reportTypeId, template, dispatch])

    const handleConciseRequest = useCallback((text, userId) => {
        localDispatch({ type: ACTION_TYPES.SET_CONCISE_INPUT, payload: '' })
        dispatch(conciseReport(text, userId, reportTypeId))
    }, [reportTypeId, dispatch])

    const handleSelectFeedback = useCallback((userId) => {
        if (state.lastConciseInput !== state.editConciseInput) {
            localDispatch({ type: ACTION_TYPES.SET_FEEDBACK_INPUT, payload: '' })
            localDispatch({ type: ACTION_TYPES.SET_LAST_CONCISE_INPUT, payload: state.editConciseInput })
            dispatch(feedbackReport(state.editConciseInput, userId, reportTypeId, state.reportConfig))
        }
    }, [state.lastConciseInput, state.editConciseInput, reportTypeId, dispatch])

    const prevStep = useCallback(() => {
        if (state.stepValue === 3 && template === -1) {
            localDispatch({ type: ACTION_TYPES.SET_STEP, payload: state.stepValue - 2 })
        } else {
            localDispatch({ type: ACTION_TYPES.SET_STEP, payload: state.stepValue - 1 })
        }
    }, [state.stepValue, template])

    const nextStep = useCallback(() => {
        if (state.stepValue === 0) {
            if (state.allTranscribes.length < MIN_CHARS) {
                localDispatch({ type: ACTION_TYPES.SHOW_SNACKBAR, payload: 'Please enter a valid narrative.' })
                return
            }
            if (state.allTranscribes.length > MAX_CHARS) {
                localDispatch({ type: ACTION_TYPES.SHOW_SNACKBAR, payload: `Maximum number of characters is limited to ${MAX_CHARS}.` })
                return
            }
            handleGenerate()
        }

        const userId = userInfo?.id || -1

        if (state.stepValue === 1) {
            if (template === -1) {
                localDispatch({ type: ACTION_TYPES.SET_STEP, payload: state.stepValue + 2 })
                localDispatch({ type: ACTION_TYPES.SET_MAX_STEP, payload: state.stepValue + 2 })
                handleConciseRequest(state.editableChartText, userId)
                return
            } else {
                handleApplyTemplate(userId)
            }
        } else if (state.stepValue === 2) {
            handleConciseRequest(templateText?.Template?.Combined || '', userId)
        } else if (state.stepValue === 3) {
            handleSelectFeedback(userId)
        }

        localDispatch({ type: ACTION_TYPES.SET_STEP, payload: state.stepValue + 1 })
        localDispatch({ type: ACTION_TYPES.SET_MAX_STEP, payload: state.stepValue + 1 })
    }, [state.stepValue, state.allTranscribes, state.editableChartText, template, userInfo,
        handleGenerate, handleApplyTemplate, handleConciseRequest, handleSelectFeedback, templateText])

    const handleStepClick = useCallback((newStep) => {
        if (newStep === 2 && template === -1) {
            return
        }
        localDispatch({ type: ACTION_TYPES.SET_STEP, payload: newStep })
    }, [template])

    const handleUndo = useCallback(() => {
        localDispatch({ type: ACTION_TYPES.UNDO_TRANSCRIBE })
    }, [])

    const handleExampleButtonClick = useCallback((event) => {
        localDispatch({ type: ACTION_TYPES.OPEN_MENU, payload: event.currentTarget })
    }, [])

    const handleMenuClose = useCallback(() => {
        localDispatch({ type: ACTION_TYPES.CLOSE_MENU })
    }, [])

    const handleSampleSelect = useCallback((sampleContent) => {
        localDispatch({ type: ACTION_TYPES.RESET_ALL_TEXTS })
        localDispatch({ type: ACTION_TYPES.UPDATE_TRANSCRIBES, payload: sampleContent })
        localDispatch({ type: ACTION_TYPES.CLOSE_MENU })
        localDispatch({ type: ACTION_TYPES.SHOW_SNACKBAR, payload: 'Sample narrative loaded. Click Next to create a report.' })
    }, [])

    const handleClose = useCallback((event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        localDispatch({ type: ACTION_TYPES.HIDE_SNACKBAR })
    }, [])

    // Memoized components
    const snackbarAction = useMemo(() => (
        <Fragment>
            <IconButton
                size="small"
                aria-label="Dismiss message"
                color="inherit"
                onClick={handleClose}
            >
                <X size={16} aria-hidden="true" />
            </IconButton>
        </Fragment>
    ), [handleClose])

    const typeLabel = reportType[reportTypeId] || 'Report'

    // The rail is derived from the same step indices the handlers use. Without a
    // template, step 2 never runs, so it is dropped and the rest renumber.
    const stages = useMemo(() => {
        const all = [
            { step: 0, label: 'Narrative' },
            { step: 1, label: `${typeLabel} report` },
            { step: 2, label: 'Template applied' },
            { step: 3, label: 'Concise' },
            { step: 4, label: 'Review' },
        ]
        const visible = template === -1 ? all.filter(s => s.step !== 2) : all
        return visible.map((s, i) => ({ ...s, n: `${i + 1}.0` }))
    }, [template, typeLabel])

    // One config per step replaces the five near-identical TextFields.
    const editor = useMemo(() => {
        const byStep = {
            0: {
                label: 'Narrative',
                value: state.allTranscribes,
                action: ACTION_TYPES.UPDATE_TRANSCRIBES,
                placeholder: `Type or dictate the call. It becomes a ${typeLabel} report when you continue.`,
                helper: 'Plain language is fine — abbreviations get applied later.',
                counted: true,
            },
            1: {
                label: `${typeLabel} report`,
                value: state.editableChartText,
                action: ACTION_TYPES.SET_EDITABLE_CHART_TEXT,
                helper: 'Edit anything here before it goes on. Your changes carry to the next stage.',
            },
            2: {
                label: 'Template applied',
                value: state.editTemplateInput,
                action: ACTION_TYPES.SET_TEMPLATE_INPUT,
                helper: 'Your service template is merged in. Where the two conflict, your narrative wins.',
            },
            3: {
                label: 'Concise report',
                value: state.editConciseInput,
                action: ACTION_TYPES.SET_CONCISE_INPUT,
                helper: 'Standard abbreviations have been applied.',
            },
            4: {
                label: 'Review notes',
                value: state.editFeedbackInput,
                action: ACTION_TYPES.SET_FEEDBACK_INPUT,
                helper: 'Notes on what is missing or inconsistent. You decide what to act on.',
            },
        }
        return byStep[state.stepValue] || null
    }, [state.stepValue, state.allTranscribes, state.editableChartText, state.editTemplateInput,
        state.editConciseInput, state.editFeedbackInput, typeLabel])

    const charCount = state.allTranscribes.length
    const countState = charCount > MAX_CHARS ? 'over' : charCount > MAX_CHARS * 0.9 ? 'near' : 'ok'

    return (
        <>
            <SEO
                title="Create a report — PCRAssist"
                description="Turn an EMS field narrative into a CHART, SOAP, or DCHART patient care report. Dictate or type the call, merge your service template, and review the draft before you sign it."
                canonicalUrl="/create"
            />

            <div className="cs">
                <div className="cs__wrap">
                    {errors.length > 0 && (
                        <div className="cs__errors">
                            {errors.map((error, index) => (
                                <div className="cs__error" key={index} role="alert">{error}</div>
                            ))}
                        </div>
                    )}

                    <header className="cs__head">
                        <div className="cs__head-row">
                            <h1 className="cs__title">New report</h1>
                            {reportType[reportTypeId] && (
                                <span className="cs__chip">{reportType[reportTypeId]}</span>
                            )}
                        </div>

                        <ol className="cs__rail">
                            {stages.map((s) => (
                                <li className="cs__rail-item" key={s.step}>
                                    <button
                                        type="button"
                                        className="cs__stage"
                                        aria-current={state.stepValue === s.step ? 'step' : undefined}
                                        data-done={state.maxStepValue > s.step}
                                        disabled={s.step > state.maxStepValue}
                                        onClick={() => handleStepClick(s.step)}
                                    >
                                        <span className="cs__stage-n">{s.n}</span>
                                        <span className="cs__stage-label">{s.label}</span>
                                    </button>
                                </li>
                            ))}
                        </ol>
                    </header>

                    {editor && (
                        <section className="cs__surface">
                            <div className="cs__surface-bar">
                                <label className="cs__label" htmlFor="cs-editor">{editor.label}</label>
                                {editor.counted && (
                                    <span className="cs__count" data-state={countState}>
                                        {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()}
                                    </span>
                                )}
                            </div>

                            <textarea
                                id="cs-editor"
                                className="cs__editor"
                                value={editor.value}
                                placeholder={editor.placeholder}
                                aria-describedby="cs-helper"
                                onChange={(e) => localDispatch({ type: editor.action, payload: e.target.value })}
                            />

                            <p className="cs__helper" id="cs-helper" role="status" data-state={countState === 'over' ? 'over' : undefined}>
                                {countState === 'over'
                                    ? `That is ${(charCount - MAX_CHARS).toLocaleString()} characters over the limit. Trim it before continuing.`
                                    : editor.helper}
                            </p>
                        </section>
                    )}

                    {state.stepValue === 0 && (
                        <>
                            <div className="cs__tools">
                                <button
                                    type="button"
                                    className="cs__btn cs__btn--rec"
                                    data-recording={isRecording}
                                    onClick={handleStartStop}
                                >
                                    {isRecording
                                        ? <><span className="cs__rec-dot" aria-hidden="true" />Stop recording</>
                                        : <><Mic size={16} aria-hidden="true" />Dictate</>}
                                </button>

                                <button
                                    type="button"
                                    className="cs__btn"
                                    onClick={handleUndo}
                                    disabled={state.prevAllTranscribes.length === 0}
                                >
                                    <Undo2 size={16} aria-hidden="true" />
                                    Undo dictation
                                </button>

                                <button
                                    type="button"
                                    className="cs__btn"
                                    onClick={handleExampleButtonClick}
                                    aria-haspopup="menu"
                                    aria-expanded={state.menuOpen}
                                >
                                    <FileText size={16} aria-hidden="true" />
                                    Load an example
                                    <ChevronDown size={16} aria-hidden="true" />
                                </button>
                            </div>

                            {!userInfo && (
                                <p className="cs__hint">
                                    New here? Load an example narrative to see the whole flow before writing your own.
                                </p>
                            )}

                            <Menu
                                anchorEl={state.anchorEl}
                                open={state.menuOpen}
                                onClose={handleMenuClose}
                                slotProps={{ paper: { sx: { maxHeight: 320, width: 320 } } }}
                            >
                                {sampleNarratives.map((sample, index) => (
                                    <MenuItem
                                        key={index}
                                        onClick={() => handleSampleSelect(sample.content)}
                                        sx={{ whiteSpace: 'normal', display: 'block', py: 1.5 }}
                                    >
                                        <strong>{sample.title}</strong>
                                        <br />
                                        <small>{sample.content.substring(0, 100)}…</small>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}

                    <div className="cs__nav">
                        <button
                            type="button"
                            className="cs__btn"
                            disabled={state.stepValue === 0}
                            onClick={prevStep}
                        >
                            <ArrowLeft size={16} aria-hidden="true" />
                            Back
                        </button>

                        <span className="cs__nav-spacer" />

                        <button
                            type="button"
                            className="cs__btn cs__btn--primary"
                            disabled={state.stepValue === 4}
                            onClick={nextStep}
                        >
                            Next
                            <ArrowRight size={16} aria-hidden="true" />
                        </button>
                    </div>

                    <footer className="cs__foot">
                        Have suggestions? <a href="/contact">Tell us what would make this better.</a>
                    </footer>
                </div>

                {isLoading && <BackdropProgress />}

                <Snackbar
                    open={state.openSnackbar}
                    autoHideDuration={5000}
                    onClose={handleClose}
                    message={state.snackbarMessage}
                    action={snackbarAction}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                />
            </div>
        </>
    )
}

export default CreateScreen
